import { Radio, Stack } from '@mui/joy';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAddContactGroupByEmailContext } from 'contacts/components/AddContactGroupByEmail/state';
import { UpsertContact } from 'contacts/components/UpsertContact';
import { Contact } from 'contacts/elements';
import { useGetContacts } from 'contacts/hooks';
import { IContact } from 'contacts/types';
import { Button, ExpandableCard } from 'core/components';
import { AddButton, FlexBox, Loader } from 'core/elements';
import { Color, Variant } from 'theme/enums';

interface IProps {
  level?: number;
}

export const Contacts = ({ level = 0 }: IProps) => {
  const [isAddContactOpen, setIsAddContactOpen] = useState(false);
  const [editContact, setEditContact] = useState<IContact | null>(null);

  const { t } = useTranslation();

  const {
    contact,
    selectedContact,
    setSelectedContact,
    contactGroupType,
    contactEmail,
    contactGroup,
    searchContact,
    attachDomainConfirmed,
  } = useAddContactGroupByEmailContext();

  const { records, isLoading } = useGetContacts(
    {
      filters: {
        groupId: contactGroup?.id,
        deleted: '"0"',
      },
      includeContactGroups: 0,
    },
    {
      enabled: !!contactGroup?.id,
    }
  );

  useEffect(() => {
    if (contact) {
      setEditContact(contact);

      return;
    }

    if (contactGroup) {
      setIsAddContactOpen(true);
    }
  }, [contact, contactEmail, contactGroup]);

  const initialValues = useMemo(() => ({ email: contactEmail }), [contactEmail]);

  if (isLoading) {
    return <Loader />;
  }

  return (
    <>
      <Stack gap={2}>
        <AddButton title={t('buttonLabelAddContact')} onClick={() => setIsAddContactOpen(true)} />

        {records?.map((record) => {
          const getContactCard = (isSelected = false) => (
            <ExpandableCard
              title={record.name}
              {...(isSelected ? { color: Color.SUCCESS, variant: Variant.SOFT } : {})}
            >
              <>
                <Stack gap={0}>
                  <Contact contact={record} contactGroupType={contactGroupType} />
                </Stack>

                <Button
                  text={t('buttonLabelEditContact')}
                  variant={Variant.PLAIN}
                  color={Color.PRIMARY}
                  onClick={() => setEditContact(record)}
                  sx={{
                    width: 'fit-content',
                    paddingRight: 0,
                    paddingLeft: 0,
                    ':hover': { backgroundColor: 'transparent', fontWeight: 800 },
                  }}
                />
              </>
            </ExpandableCard>
          );

          if (!searchContact) {
            return getContactCard();
          }

          const isSelected = record.id === selectedContact?.id;

          return (
            <FlexBox key={record.id}>
              <Radio
                color={isSelected ? Color.SUCCESS : Color.PRIMARY}
                value={record.id}
                checked={isSelected}
                label={getContactCard(isSelected)}
                onChange={() => setSelectedContact(record)}
                slotProps={{
                  radio: {
                    sx: { marginTop: '12px' },
                  },
                }}
                sx={{ width: '100%' }}
              />
            </FlexBox>
          );
        })}
      </Stack>

      <UpsertContact
        isOpen={isAddContactOpen || !!editContact?.id}
        onClose={() => {
          setIsAddContactOpen(false);
          setEditContact(null);
        }}
        contact={editContact || undefined}
        contactGroup={contactGroup}
        onSubmitSuccess={(contact) => {
          setEditContact(null);
          setSelectedContact(contact);
        }}
        initialValues={initialValues}
        level={level + 1}
        attachDomainConfirmed={attachDomainConfirmed}
      />
    </>
  );
};
